import { Box, Stack, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  InfoOutlined,
  NotificationsActiveOutlined,
  NotificationsOffOutlined,
  ErrorOutline,
} from '@mui/icons-material';
import { ReactNode } from 'react';

/**
 * How a toast point reads at a glance:
 * - `done`     — sorted (tick)
 * - `todo`     — still needs doing (amber)
 * - `notified` — someone was told, or will be (bell)
 * - `silent`   — nobody was told (crossed-out bell)
 * - `info`     — worth knowing (i)
 */
export type ToastPointTone = 'done' | 'todo' | 'notified' | 'silent' | 'info';

export interface ToastPoint {
  tone: ToastPointTone;
  text: string;
}

const ICON: Record<ToastPointTone, ReactNode> = {
  done: <CheckCircleOutline fontSize="inherit" color="success" />,
  todo: <ErrorOutline fontSize="inherit" color="warning" />,
  notified: <NotificationsActiveOutlined fontSize="inherit" color="action" />,
  silent: <NotificationsOffOutlined fontSize="inherit" color="action" />,
  info: <InfoOutlined fontSize="inherit" color="action" />,
};

/** What matters most when there's more to say than fits. */
const PRIORITY: Record<ToastPointTone, number> = {
  todo: 0,
  silent: 1,
  notified: 2,
  done: 3,
  info: 4,
};

/** At most this many lines — a toast is a glance, not a report. */
export const MAX_TOAST_POINTS = 3;

/**
 * The points a toast shows: the most important few, kept in their given
 * order so the toast still reads naturally.
 */
export function pickToastPoints(
  points: Array<ToastPoint | null | undefined | false>,
  max = MAX_TOAST_POINTS,
): ToastPoint[] {
  const all = points.filter((p): p is ToastPoint => !!p && !!p.text);
  const keep = new Set(
    [...all]
      .map((p, i) => ({ p, i }))
      .sort((a, b) => PRIORITY[a.p.tone] - PRIORITY[b.p.tone] || a.i - b.i)
      .slice(0, max)
      .map(({ p }) => p),
  );
  return all.filter((p) => keep.has(p));
}

/**
 * A toast body with more than one thing to say: one short lead sentence,
 * then a few scannable lines, each with an icon for what kind of fact it
 * is. A run of separate sentences read as a recital; this reads as a status.
 * Past MAX_TOAST_POINTS, the least important lines are dropped.
 */
export function ToastBody({
  lead,
  points = [],
}: {
  lead?: string | null;
  points?: Array<ToastPoint | null | undefined | false>;
}) {
  const shown = pickToastPoints(points);
  return (
    <Box>
      {lead && (
        <Typography component="div" variant="body2" color="text.primary">
          {lead}
        </Typography>
      )}
      {shown.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: lead ? 0.75 : 0 }}>
          {shown.map((p, i) => (
            <Stack
              key={i}
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              // One font size for icon box and text, so "one line tall" means
              // the same for both and the icon centres on the first line.
              sx={{ typography: 'body2', lineHeight: 1.45 }}
            >
              <Box
                component="span"
                sx={{
                  height: '1.45em',
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  '& svg': { fontSize: '1.15em' },
                }}
              >
                {ICON[p.tone]}
              </Box>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 'inherit' }}
              >
                {p.text}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/** The words a ToastBody shows, for working out how long to keep it up. */
export function toastBodyText(
  lead?: string | null,
  points: Array<ToastPoint | null | undefined | false> = [],
): string {
  return [lead, ...pickToastPoints(points).map((p) => p.text)]
    .filter(Boolean)
    .join(' ');
}
